import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHttpClient } from '../../../shared/hooks/http-hook';
import { PasswordState, PasswordFieldConfig, UsePasswordChangeReturn } from './types';
import { usePasswordVisibility } from './usePasswordVisibility';
import { useScreenReaderAnnouncement } from './useScreenReaderAnnouncement';

const PASSWORD_VALIDATOR = /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.{8,}).*/;
const GREEN_CHECK_DISPLAY_DURATION = 2000;

export const usePasswordChange = (): UsePasswordChangeReturn => {
  const { sendRequest } = useHttpClient();
  const { t } = useTranslation();
  const [passwordState, setPasswordState] = useState<PasswordState>({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    validPassword: false,
    passwordsMatch: false,
    changeFailed: false,
    currentPasswordRequired: false,
    newPasswordRequired: false,
    confirmNewPasswordRequired: false,
  });

  const [showGreenCheck, setShowGreenCheck] = useState(false);
  const [isAfterSubmit, setIsAfterSubmit] = useState(false);
  const [errorSummary, setErrorSummary] = useState<string[]>([]);

  const [showCurrentPassword, toggleShowCurrentPassword, announcementCurrentPassword] =
    usePasswordVisibility();
  const [showNewPassword, toggleShowNewPassword, announcementNewPassword] = usePasswordVisibility();
  const [showConfirmPassword, toggleShowConfirmPassword, announcementConfirmPassword] =
    usePasswordVisibility();

  const { announcement, announceError, clearAnnouncement } = useScreenReaderAnnouncement();

  const validatePassword = (password: string): boolean => {
    return PASSWORD_VALIDATOR.test(password);
  };

  const validatePasswordsMatch = (password: string, confirmPassword: string): boolean => {
    return password === confirmPassword;
  };

  const handlePasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPasswordState((prevState) => {
      const updatedState = {
        ...prevState,
        [name]: value,
      };

      // Reset required validation flags when user starts typing
      if (name === 'currentPassword' && value.trim()) {
        updatedState.currentPasswordRequired = false;
        if (prevState.changeFailed) {
          updatedState.changeFailed = false;
        }
      } else if (name === 'newPassword' && value.trim()) {
        updatedState.newPasswordRequired = false;
        updatedState.validPassword = validatePassword(value);
        updatedState.passwordsMatch = validatePasswordsMatch(value, prevState.confirmNewPassword);
      } else if (name === 'confirmNewPassword' && value.trim()) {
        updatedState.confirmNewPasswordRequired = false;
        updatedState.passwordsMatch = validatePasswordsMatch(value, prevState.newPassword);
      }

      return updatedState;
    });
  }, []);

  const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorSummary([]);

    // Reset isAfterSubmit to ensure focus works on subsequent submissions
    setIsAfterSubmit(false);

    // Collect all validation errors first
    const errors = [];
    const newState = { ...passwordState };

    // Check required fields
    if (!passwordState.currentPassword.trim()) {
      errors.push('currentPassword');
      newState.currentPasswordRequired = true;
    } else {
      newState.currentPasswordRequired = false;
    }

    if (!passwordState.newPassword.trim()) {
      errors.push('newPassword');
      newState.newPasswordRequired = true;
    } else {
      newState.newPasswordRequired = false;
    }

    if (!passwordState.confirmNewPassword.trim()) {
      errors.push('confirmNewPassword');
      newState.confirmNewPasswordRequired = true;
    } else {
      newState.confirmNewPasswordRequired = false;
    }

    // Validate new password format if provided
    if (passwordState.newPassword.trim() && !validatePassword(passwordState.newPassword)) {
      newState.validPassword = false;
      if (!errors.includes('newPassword')) {
        errors.push('newPassword');
      }
    } else if (passwordState.newPassword.trim()) {
      newState.validPassword = true;
    }

    // Validate password match if both passwords are provided
    if (passwordState.newPassword.trim() && passwordState.confirmNewPassword.trim()) {
      const passwordsMatch = validatePasswordsMatch(
        passwordState.newPassword,
        passwordState.confirmNewPassword
      );
      newState.passwordsMatch = passwordsMatch;
      if (!passwordsMatch && !errors.includes('confirmNewPassword')) {
        errors.push('confirmNewPassword');
      }
    }

    // If current password is provided, validate it on backend
    if (passwordState.currentPassword.trim()) {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/users/profile/credentials`,
          'POST',
          JSON.stringify({
            currentCode: passwordState.currentPassword,
            newCode: passwordState.newPassword || '', // Send empty string if not provided
            type: 'password',
          })
        );

        if (responseData) {
          setShowGreenCheck(true);
          setTimeout(() => {
            setShowGreenCheck(false);
          }, GREEN_CHECK_DISPLAY_DURATION);
        }
      } catch (err) {
        if (err.status === 403) {
          newState.changeFailed = true;
        } else {
          newState.changeFailed = true;
        }
        // Add current password error to the list
        if (!errors.includes('currentPassword')) {
          errors.push('currentPassword');
        }
      }
    }

    // Set all state changes at once
    setPasswordState(newState);

    // Set error summary with all errors
    setErrorSummary(errors);

    // Show all errors at once
    setTimeout(() => {
      setIsAfterSubmit(true);
    }, 0);
  };

  const getFieldError = useCallback(
    (fieldName: keyof PasswordState): { hasError: boolean; message: string | React.ReactNode } => {
      // Check for required field validation first
      if (
        fieldName === 'currentPassword' &&
        passwordState.currentPasswordRequired &&
        !passwordState.currentPassword.trim()
      ) {
        return {
          hasError: true,
          message: 'Current password is required',
        };
      }

      if (
        fieldName === 'newPassword' &&
        passwordState.newPasswordRequired &&
        !passwordState.newPassword.trim()
      ) {
        return {
          hasError: true,
          message: t('signup.password.required'),
        };
      }

      if (
        fieldName === 'confirmNewPassword' &&
        passwordState.confirmNewPasswordRequired &&
        !passwordState.confirmNewPassword.trim()
      ) {
        return {
          hasError: true,
          message: t('signup.confirmPassword.required'),
        };
      }

      // Then check for other validation errors
      if (fieldName === 'currentPassword' && passwordState.changeFailed) {
        return {
          hasError: true,
          message: t('profile.settings.securityDetails.changePassword.currentPassword.error'),
        };
      }

      if (
        fieldName === 'newPassword' &&
        passwordState.newPassword.length > 0 &&
        !passwordState.validPassword
      ) {
        return {
          hasError: true,
          message: t('S033'),
        };
      }

      if (
        fieldName === 'confirmNewPassword' &&
        passwordState.confirmNewPassword.length > 0 &&
        !passwordState.passwordsMatch
      ) {
        return {
          hasError: true,
          message: t('profile.settings.securityDetails.changePasscode.rules.confirmNewPasscode'),
        };
      }

      return { hasError: false, message: '' };
    },
    [passwordState, t]
  );

  const handleFieldBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name } = event.target;
      const fieldName = name as keyof PasswordState;
      const { hasError, message } = getFieldError(fieldName);

      if (hasError && message) {
        announceError(typeof message === 'string' ? message : 'Validation error');
      } else {
        clearAnnouncement();
      }
    },
    [getFieldError, announceError, clearAnnouncement]
  );

  const passwordFields: PasswordFieldConfig[] = [
    {
      name: 'currentPassword',
      descriptionKey: 'profile.settings.securityDetails.changePassword.currentPassword',
      showPassword: showCurrentPassword,
      toggleFunction: toggleShowCurrentPassword,
      announcement: announcementCurrentPassword,
    },
    {
      name: 'newPassword',
      descriptionKey: 'profile.settings.securityDetails.changePassword.newPassword',
      showPassword: showNewPassword,
      toggleFunction: toggleShowNewPassword,
      rulesDescription: t('profile.settings.securityDetails.changePassword.rules.newPassword'),
      announcement: announcementNewPassword,
    },
    {
      name: 'confirmNewPassword',
      descriptionKey: 'profile.settings.securityDetails.changePassword.confirmNewPassword',
      showPassword: showConfirmPassword,
      toggleFunction: toggleShowConfirmPassword,
      rulesDescription: t('profile.settings.securityDetails.changePassword.rules.confirmPassword'),
      announcement: announcementConfirmPassword,
    },
  ];

  return {
    errorSummary,
    passwordState,
    showGreenCheck,
    isAfterSubmit,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    announcementCurrentPassword,
    announcementNewPassword,
    announcementConfirmPassword,
    handlePasswordChange,
    handleChangePassword,
    handleFieldBlur,
    getFieldError,
    passwordFields,
    announcement,
  };
};
